const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:7016/api';

// In-memory cache to avoid re-fetching content on repeated navigations within the same session
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const contentCache = new Map<string, ContentDocument[]>();
const contentCacheTimes = new Map<string, number>();
const contentFetchPromises = new Map<string, Promise<ContentDocument[]>>();

export interface LegalArea {
  id: string;
  de?: string;
  fr?: string;
  en?: string;
}

export interface TranslationContent {
  isPublished: boolean;
  // Email template fields (one document per legal area, slug = area id or "general")
  notificationSubject?: string;
  notificationBodyHtml?: string;
  notificationBodyText?: string;
  confirmationSubject?: string;
  confirmationBodyHtml?: string;
  confirmationBodyText?: string;
  // Portrait
  name?: string;
  title?: string;
  biography?: string;
  introText?: string;
  areasOfExpertise?: string[];
  languages?: string[];
  ctaText?: string;
  // FAQ
  question?: string;
  answer?: string;
  // Glossary
  term?: string;
  explanation?: string;
  // Blog
  slug?: string;
  excerpt?: string;
  content?: string;
  seoTitle?: string;
  seoDescription?: string;
  disclaimer?: string;
  // Impressum
  companyName?: string;
  address?: string;
  phone?: string;
  email?: string;
  registrationNumber?: string;
  additionalHtml?: string;
  // Settings
  urlPrivacy?: string;
  urlTerms?: string;
  urlCancellation?: string;
  // Booking email templates (email-template type, slug "booking" fields live alongside contact ones)
  bookingConfirmationSubject?: string;
  bookingConfirmationBodyHtml?: string;
  bookingConfirmationBodyText?: string;
  bookingCancellationSubject?: string;
  bookingCancellationBodyHtml?: string;
  bookingCancellationBodyText?: string;
  bookingNotificationSubject?: string;
  bookingNotificationBodyHtml?: string;
  bookingNotificationBodyText?: string;
  bookingDocumentsSubject?: string;
  bookingDocumentsBodyHtml?: string;
  bookingDocumentsBodyText?: string;
  // Appointment-type translated fields
  priceInfo?: string;
}

export interface ContentDocument {
  id: string;
  type: string;
  isPublished: boolean;
  sortOrder: number;
  isHighlighted?: boolean;
  category?: string;
  categories?: string[];
  slug?: string;
  createdAt: string;
  updatedAt: string;
  translations: Record<string, TranslationContent>;
  // Settings — areas of law
  legalAreas?: LegalArea[];
  // Settings — email
  emailNotificationAddress?: string;
  emailSenderName?: string;
  emailNotificationAttachmentUrls?: string[];
  emailConfirmationAttachmentUrls?: string[];
  // Blog-specific
  author?: string;
  featuredImageUrl?: string;
  publicationDate?: string;
  tags?: string[];
  // Portrait-specific
  portraitImageUrl?: string;
  // Booking settings (type: "booking-settings")
  timeZoneId?: string;
  openingHours?: OpeningHoursRule[];
  exceptions?: OpeningHoursException[];
  slotGranularityMinutes?: number;
  leadTimeMinutes?: number;
  maxAdvanceDays?: number;
  bufferBeforeMinutes?: number;
  bufferAfterMinutes?: number;
  maxBookingsPerDay?: number;
  requireApproval?: boolean;
  holdMinutes?: number;
  cancellationCutoffHours?: number;
  failClosed?: boolean;
  blockingShowAs?: string[];
  includeBookingCalendarAsBlocking?: boolean;
  createContactSubmission?: boolean;
  bookingNotificationEmail?: string;
  // Appointment type (type: "appointment-type")
  durationMinutes?: number;
  mode?: string;
  areaOfLaw?: string;
  colorHex?: string;
  openingHoursOverride?: OpeningHoursRule[];
}

export interface TimeRange {
  start: string; // "HH:mm"
  end: string;
}

export interface OpeningHoursRule {
  weekday: number; // 0 = Sunday … 6 = Saturday
  ranges: TimeRange[];
}

export interface OpeningHoursException {
  date: string; // yyyy-MM-dd
  closed: boolean;
  ranges?: TimeRange[];
}

export interface ContactFormData {
  name: string;
  email: string;
  phone?: string;
  street?: string;
  zip?: string;
  city?: string;
  country?: string;
  areaOfLaw?: string;
  subject: string;
  message: string;
  preferredLanguage: string;
  consent: boolean;
  website?: string; // honeypot
}

export interface SubmissionSummary {
  id: string;
  name: string;
  areaOfLaw?: string;
  subject: string;
  submittedAt: string;
  status: string;
  isClosed: boolean;
  updateCount: number;
}

/**
 * Synchronously read cached content for a type. Returns null if not yet cached.
 */
export function getContentFromCache(type: string): ContentDocument[] | null {
  return contentCache.get(type) ?? null;
}

/**
 * Fetch all published content items of a given type.
 * Returns from cache if fresh (< CACHE_TTL_MS). Re-fetches when stale so pages
 * always reflect recent CMS changes (stale-while-revalidate via lazy initialiser).
 */
export async function getContentByType(type: string): Promise<ContentDocument[]> {
  const now = Date.now();
  const cachedAt = contentCacheTimes.get(type);
  if (contentCache.has(type) && cachedAt !== undefined && (now - cachedAt) < CACHE_TTL_MS) {
    return contentCache.get(type)!;
  }
  // Stale or no cache — deduplicate concurrent requests for the same type
  if (!contentFetchPromises.has(type)) {
    const promise = fetch(`${API_URL}/content/${type}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch ${type} content: ${response.status}`);
        }
        return response.json() as Promise<ContentDocument[]>;
      })
      .then((data) => {
        contentCache.set(type, data);
        contentCacheTimes.set(type, Date.now());
        contentFetchPromises.delete(type);
        return data;
      })
      .catch((err) => {
        contentFetchPromises.delete(type);
        throw err;
      });
    contentFetchPromises.set(type, promise);
  }
  return contentFetchPromises.get(type)!;
}

/**
 * Warm up the content cache for the given types. Safe to call multiple times.
 * Skips types that are already cached and still within the TTL window.
 */
export function preloadContentTypes(types: string[]): void {
  const now = Date.now();
  types.forEach((type) => {
    const cachedAt = contentCacheTimes.get(type);
    const isFresh = cachedAt !== undefined && (now - cachedAt) < CACHE_TTL_MS;
    if (!isFresh && !contentFetchPromises.has(type)) {
      getContentByType(type).catch(() => { /* silently ignore preload errors */ });
    }
  });
}

/**
 * Fetch a single content item by type and id.
 */
export async function getContentById(type: string, id: string): Promise<ContentDocument | null> {
  const response = await fetch(`${API_URL}/content/${type}/${id}`);
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(`Failed to fetch ${type}/${id}: ${response.status}`);
  }
  return response.json();
}

/**
 * Fetch a blog post by slug.
 */
export async function getBlogBySlug(slug: string): Promise<ContentDocument | null> {
  const response = await fetch(`${API_URL}/content/blog/by-slug/${slug}`);
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(`Failed to fetch blog post: ${response.status}`);
  }
  return response.json();
}

/**
 * Submit a contact form (multipart/form-data to support optional file attachments).
 */
export async function submitContactForm(
  data: ContactFormData,
  files: File[] = []
): Promise<{ message: string }> {
  const formData = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, String(value));
    }
  });
  files.forEach((file) => formData.append('files', file));

  const response = await fetch(`${API_URL}/contact`, {
    method: 'POST',
    body: formData,
    // Do NOT set Content-Type — browser sets it with correct multipart boundary
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.errors?.join(', ') || `Submit failed: ${response.status}`);
  }
  return response.json();
}

/**
 * Get a limited public summary of a submission for the follow-up page.
 */
export async function getSubmissionSummary(
  id: string,
  token: string
): Promise<SubmissionSummary> {
  const response = await fetch(
    `${API_URL}/contact/${encodeURIComponent(id)}/summary?token=${encodeURIComponent(token)}`
  );
  if (response.status === 401) throw new Error('invalidToken');
  if (response.status === 404) throw new Error('notFound');
  if (!response.ok) throw new Error(`Failed to load summary: ${response.status}`);
  return response.json();
}

/**
 * Submit a follow-up update to an existing submission.
 */
export async function submitSubmissionUpdate(
  id: string,
  token: string,
  message: string,
  files: File[] = []
): Promise<{ message: string; updateCount: number; status: string }> {
  const formData = new FormData();
  formData.append('message', message);
  files.forEach((file) => formData.append('files', file));

  const response = await fetch(
    `${API_URL}/contact/${encodeURIComponent(id)}/update?token=${encodeURIComponent(token)}`,
    { method: 'POST', body: formData }
  );
  if (response.status === 403) throw new Error('closed');
  if (response.status === 401) throw new Error('invalidToken');
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.errors?.join(', ') || `Update failed: ${response.status}`);
  }
  return response.json();
}

/**
 * Get the translation for a content document in the given language.
 * Only returns the translation if it is explicitly published for the requested language.
 */
export function getTranslation(
  doc: ContentDocument,
  lang: string
): TranslationContent | null {
  const translation = doc.translations?.[lang];
  if (translation && translation.isPublished) return translation;

  return null;
}

/**
 * Check if a content document has a published translation for the given language.
 */
export function hasTranslation(doc: ContentDocument, lang: string): boolean {
  return doc.translations?.[lang]?.isPublished === true;
}
